/**
 * Formats a time string (e.g. HH:mm:ss or ISO8601) to a valid HH:mm string for the input field.
 */
export const formatTimeForInput = (timeString: string | null | undefined): string => {
  if (!timeString) return '09:00';

  // If it's already HH:mm
  if (/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(timeString)) {
    return timeString;
  }

  // If it's HH:mm:ss or HH:mm:ss.sssZ
  if (/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]/.test(timeString)) {
    return timeString.substring(0, 5);
  }

  // Parse ISO
  try {
    const d = new Date(timeString);
    if (!isNaN(d.getTime())) {
      const h = String(d.getHours()).padStart(2, '0');
      const m = String(d.getMinutes()).padStart(2, '0');
      return `${h}:${m}`;
    }
  } catch (e) {
    // Ignore error
  }

  return '09:00';
};

/**
 * Converts a time string (HH:mm or HH:mm:ss) into an API time string (HH:mm:ss).
 * Removes timezone info as per backend requirements.
 */
export const convertToApiTime = (time: string | null | undefined): string => {
  if (!time) return '09:00:00';

  // Extract HH:mm:ss and ignore timezone/milliseconds
  const noTz = time.split('Z')[0].split('+')[0].split('-')[0]; // basic strip
  const parts = noTz.split(':');
  const hh = (parts[0] || '09').padStart(2, '0');
  const mm = (parts[1] || '00').padStart(2, '0');
  const ss = (parts[2] || '00').split('.')[0].padStart(2, '0');

  return `${hh}:${mm}:${ss}`;
};

/**
 * Gets the timezone offset string (e.g. +05:30) for a given IANA timezone name.
 */
export const getTimezoneOffsetString = (timezone: string, date: Date = new Date()): string => {
  try {
    // Handle special case for 'IST' if it's not recognized by the environment
    const tzName = timezone === 'IST' ? 'Asia/Kolkata' : timezone;

    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tzName,
      timeZoneName: 'longOffset',
    });

    const parts = formatter.formatToParts(date);
    const offsetPart = parts.find(part => part.type === 'timeZoneName');

    if (offsetPart) {
      // offsetPart.value is like "GMT+05:30" or "GMT-07:00" or "UTC+05:30"
      const match = offsetPart.value.match(/([+-]\d{2}:\d{2})$/);
      if (match) return match[1];

      // Fallback for formats like GMT+5 (no colon)
      const simpleMatch = offsetPart.value.match(/([+-]\d+)$/);
      if (simpleMatch) {
        const sign = simpleMatch[1].charAt(0);
        const hours = simpleMatch[1].substring(1).padStart(2, '0');
        return `${sign}${hours}:00`;
      }
    }
  } catch (e) {
    console.error('Error calculating timezone offset', e);
  }

  return '+00:00'; // Fallback to UTC
};

/**
 * Convert a wall-clock Date (its local digits = the business's wall-clock digits) to an
 * absolute ISO string using the business timezone offset, so the backend filters correctly.
 * Falls back to the browser's ISO when no timezone is provided.
 */
export const wallClockToISO = (date: Date, timezone?: string): string => {
  if (!timezone) return date.toISOString();
  const y = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const mi = String(date.getMinutes()).padStart(2, '0');
  const s = String(date.getSeconds()).padStart(2, '0');
  const ms = String(date.getMilliseconds()).padStart(3, '0');
  const offset = getTimezoneOffsetString(timezone, date);
  const abs = new Date(`${y}-${mo}-${d}T${h}:${mi}:${s}.${ms}${offset}`);
  return isNaN(abs.getTime()) ? date.toISOString() : abs.toISOString();
};

/**
 * Converts a datetime-local input string (YYYY-MM-DDTHH:mm) into ISO8601 with timezone (e.g. 2023-10-27T10:00:00+05:30).
 */
export const formatISOWithTimezone = (dateTimeLocal: string, timezone: string): string => {
  if (!dateTimeLocal) return '';

  // datetime-local is YYYY-MM-DDTHH:mm
  // We just append :00 and the offset since the user picked the time in the target timezone
  const offset = getTimezoneOffsetString(timezone, new Date(dateTimeLocal));
  return `${dateTimeLocal}:00${offset}`;
};

/**
 * Converts a Date object into a YYYY-MM-DDTHH:mm string for datetime-local inputs.
 *
 * For a plain Date (no TZ context), uses the browser's local representation.
 * For slot times received from the backend, prefer parseSlotAsWallClock() first,
 * then pass the result here so the wall-clock digits are preserved correctly.
 */
export const formatDateToDateTimeLocal = (date: Date | null | undefined): string => {
  if (!date || isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const mi = String(date.getMinutes()).padStart(2, '0');
  return `${y}-${mo}-${d}T${h}:${mi}`;
};

/**
 * Parses a TZ-aware ISO string from the backend (e.g. "2026-04-17T19:30:00+01:00")
 * and returns a Date whose local wall-clock digits match the BUSINESS timezone's
 * wall-clock, regardless of which timezone the user's browser is in.
 *
 * Example: "2026-04-17T19:30:00+01:00" → Date with getHours()===19, getMinutes()===30
 *
 * This is the canonical way to consume slot_start / slot_end from the API.
 */
export const parseSlotAsWallClock = (isoString: string | null | undefined): Date | null => {
  if (!isoString) return null;
  // Match the datetime part before any offset (+HH:MM / -HH:MM / Z)
  const match = isoString.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2})/);
  if (!match) return null;
  // Construct a local Date from the wall-clock digits — no offset arithmetic needed
  const wallClock = new Date(`${match[1]}T${match[2]}`);
  return isNaN(wallClock.getTime()) ? null : wallClock;
};
